import type AiCostLimitView from '@UI/Store/AiCostLimits/Views/AiCostLimitView';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

export const DEFAULT_ALERT_THRESHOLD_PERCENT = 80;

type AiCostLimitFormArgs = {
	subjectId: number | null;
	monthlyAmount: number | null;
	alertThresholdPercent: number | null;
};

type AiCostLimitCommandData = {
	subjectId: number;
	monthlyAmount: number;
	alertThresholdPercent: number;
};

@ValidatedClass()
class AiCostLimitFormData extends AbstractValidatedClass<AiCostLimitFormArgs> {
	@constraint(Joi.number().integer().required(), () => 'Choisissez un périmètre.')
	public readonly subjectId: number | null;

	@constraint(
		Joi.number().min(0.01).max(1000000).precision(2).required(),
		() => 'Saisissez un plafond entre 0,01 € et 1 000 000 €.',
	)
	public readonly monthlyAmount: number | null;

	@constraint(Joi.number().integer().min(1).max(99).required(), () => 'Saisissez un seuil entre 1 et 99 %.')
	public readonly alertThresholdPercent: number | null;

	constructor(data: AiCostLimitFormArgs) {
		super(data);
		this.subjectId = data.subjectId;
		this.monthlyAmount = data.monthlyAmount;
		this.alertThresholdPercent = data.alertThresholdPercent;
		Object.freeze(this);
	}

	/** `subjectId` is preset when the level has a single possible subject (the account). */
	static createEmpty(subjectId: number | null = null): AiCostLimitFormData {
		return new AiCostLimitFormData({
			subjectId,
			monthlyAmount: null,
			alertThresholdPercent: DEFAULT_ALERT_THRESHOLD_PERCENT,
		});
	}

	static fromView(view: AiCostLimitView): AiCostLimitFormData {
		return new AiCostLimitFormData({
			subjectId: view.subjectId,
			monthlyAmount: view.monthlyAmount,
			alertThresholdPercent: view.alertThresholdPercent,
		});
	}

	setSubjectId(subjectId: number | null): this {
		return this.cloneWith({ subjectId } as Partial<AiCostLimitFormArgs>);
	}

	setMonthlyAmount(monthlyAmount: number | null): this {
		return this.cloneWith({ monthlyAmount } as Partial<AiCostLimitFormArgs>);
	}

	setAlertThresholdPercent(alertThresholdPercent: number | null): this {
		return this.cloneWith({ alertThresholdPercent } as Partial<AiCostLimitFormArgs>);
	}

	/** `null` while a field is empty, which `isFormDataValid` already reports. */
	toCommandData(): AiCostLimitCommandData | null {
		if (this.subjectId === null || this.monthlyAmount === null || this.alertThresholdPercent === null) {
			return null;
		}

		return {
			subjectId: this.subjectId,
			monthlyAmount: this.monthlyAmount,
			alertThresholdPercent: this.alertThresholdPercent,
		};
	}
}

export default AiCostLimitFormData;
